import { computed, unref } from 'vue';
import type { FormProps } from '@mts/components/Form';
import type { FetchParams } from '@mts/components/Table';
import type { Fn } from '@mts-fe/types';
import type { ComputedRef, Slots } from 'vue';
import { isFunction } from '@mts-fe/utils';
import { PADMode } from '@mts/utils/env';
import { SLOT_FORM_REG } from '../helper';

export function useTableForm(
  propsRef: ComputedRef<{
    formConfig?: Partial<FormProps>;
    searchCacheKey?: string;
    handleSearchInfoFn?: Fn;
  }>,
  slots: Slots,
  fetch: (opt?: FetchParams | undefined) => Promise<Recordable<any>[] | undefined>,
  getLoading: ComputedRef<boolean | undefined>,
) {
  const getFormProps = computed((): Partial<FormProps> => {
    const { formConfig, searchCacheKey } = unref(propsRef);
    const { submitButtonOptions } = formConfig || {};
    return {
      showActionButtonGroup: true,
      showAdvancedButton: false,
      baseColProps: { span: 4 },
      autoSubmitOnEnter: true,
      compact: true,
      ...formConfig,
      hideLabel: formConfig?.hideLabel ?? PADMode,
      submitButtonOptions: { loading: unref(getLoading), ...submitButtonOptions },
      resetFunc: async () => {
        searchCacheKey && sessionStorage.removeItem(searchCacheKey);
      },
    };
  });

  const getFormSlotKeys: ComputedRef<string[]> = computed(() => {
    const keys = Object.keys(slots);
    return keys
      .map((item) => (item.startsWith('form-') ? item : null))
      .filter((item) => !!item) as string[];
  });

  function replaceFormSlotKey(key: string) {
    if (!key) return '';
    return key?.replace?.(SLOT_FORM_REG, '') ?? '';
  }

  function handleSearchInfoChange(info: Recordable) {
    const { handleSearchInfoFn } = unref(propsRef);
    if (handleSearchInfoFn && isFunction(handleSearchInfoFn)) {
      info = handleSearchInfoFn(info) || info;
    }
    fetch({ searchInfo: info, page: 1 });
  }

  return {
    getFormProps,
    replaceFormSlotKey,
    getFormSlotKeys,
    handleSearchInfoChange,
  };
}
